import { cn, Loader } from 'component-library';
import type React from 'react';
import { memo, Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useToggle } from 'usehooks-ts';

import { OrganizationUIStyle } from '@/api/rest/resources/types/organization';
import { CoPilot } from '@/components/CoPilot/CoPilot';
import { useDisplayCoPilot } from '@/components/CoPilot/hooks/useDisplayCoPilot';
import { NavigationSidebar } from '@/components/widgets/o/Sidebar/NavigationSidebar';
import { useIsConservation } from '@/hooks/useIsConservation';
import { useIsStaffUser } from '@/hooks/useIsStaffUser';
import { useResponsiveSidebar } from '@/hooks/useResponsiveSidebar';
import { useUIStyle } from '@/hooks/useUIStyle';
import { useAuth } from '@/lib/auth';
import { ImpersonatingBanner } from '@/lib/auth/components/ImpersonatingBanner';

import { SidebarToggleButton } from '../../a/Button/SidebarToggleButton';

type ResponsiveLayoutProps = {
  header?: React.ReactNode;
  className?: string;
  showImpersonatingBanner?: boolean;
};

const MIN_WIDTH_RIGHT_SIDEBAR = 320;

const SIDEBAR_CLASSES =
  'fixed bg-white-100 border-neutral-black-12 z-0 overflow-y-auto hide-scrollbar transition-all duration-200';

type LeftSidebarWrapperProps = {
  bannerHeight: number;
  onMarginChange: (marginClass: string) => void;
};

const LeftSidebarContainer = ({
  bannerHeight,
  children,
  isCollapsed = false,
  className,
  dataTestId,
}: {
  bannerHeight: number;
  children: React.ReactNode;
  isCollapsed?: boolean;
  className?: string;
  dataTestId?: string;
}) => {
  const sidebarStyle = { top: `${bannerHeight}px`, height: `calc(100vh - ${bannerHeight}px)` };
  return (
    <aside
      className={cn(SIDEBAR_CLASSES, 'left-0 border-r', isCollapsed ? 'w-20' : 'w-72', className)}
      data-testid={dataTestId}
      style={sidebarStyle}
    >
      {children}
    </aside>
  );
};

const LeftSidebarWrapper = memo(({ bannerHeight, onMarginChange }: LeftSidebarWrapperProps) => {
  const isConservation = useIsConservation();
  const isStaff = useIsStaffUser();
  const { toggle, isCollapsed } = useResponsiveSidebar('leftSidebar', 'small');

  const isVisible = !isConservation || isStaff;

  useLayoutEffect(() => {
    const isCollapsedStyle = isCollapsed ? 'ml-20' : 'ml-72';
    onMarginChange(isVisible ? isCollapsedStyle : '');
  }, [isVisible, isCollapsed, onMarginChange]);

  if (!isVisible) return null;

  return (
    <LeftSidebarContainer bannerHeight={bannerHeight} isCollapsed={isCollapsed} dataTestId='left-sidebar'>
      <NavigationSidebar isCollapsed={isCollapsed} onMinimize={toggle} />
      {isCollapsed && (
        <div className='w-full text-center py-4'>
          <SidebarToggleButton direction='right' onClick={toggle} data-testid='left-sidebar-expand-button' />
        </div>
      )}
    </LeftSidebarContainer>
  );
});
LeftSidebarWrapper.displayName = 'LeftSidebarWrapper';

type RightSidebarWrapperProps = {
  bannerHeight: number;
  onWidthChange: (width: string) => void;
};

const RightSidebarWrapper = memo(({ bannerHeight, onWidthChange }: RightSidebarWrapperProps) => {
  const displayCoPilot = useDisplayCoPilot();
  const { toggle, isCollapsed } = useResponsiveSidebar('rightSidebar', 'medium');

  const rightSidebarRef = useRef<HTMLDivElement>(null);
  const [rightSidebarWidth, setRightSidebarWidth] = useState(MIN_WIDTH_RIGHT_SIDEBAR);
  const isResizing = useRef(false);

  const sidebarStyle = { top: `${bannerHeight}px`, height: `calc(100vh - ${bannerHeight}px)` };
  const effectiveWidth = isCollapsed ? `${MIN_WIDTH_RIGHT_SIDEBAR / 4}px` : `${rightSidebarWidth}px`;

  useLayoutEffect(() => {
    onWidthChange(displayCoPilot ? effectiveWidth : '0px');
  }, [displayCoPilot, effectiveWidth, onWidthChange]);

  useEffect(() => {
    const sidebar = rightSidebarRef.current;
    if (!sidebar) return undefined;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return;

      const container = sidebar.parentElement;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const newWidth = containerRect.right - e.clientX;

      if (newWidth >= MIN_WIDTH_RIGHT_SIDEBAR / 4) {
        setRightSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      isResizing.current = false;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return (): void => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  if (!displayCoPilot) return null;

  return (
    <aside
      ref={rightSidebarRef}
      className={cn(SIDEBAR_CLASSES, 'right-0 border-l overflow-x-hidden group/sidebar')}
      data-testid='right-sidebar'
      style={{ ...sidebarStyle, width: effectiveWidth }}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className='absolute left-0 top-0 h-full w-4 cursor-col-resize z-10 flex items-center justify-center'
        onMouseDown={(e) => {
          isResizing.current = true;
          e.preventDefault();
        }}
      >
        <div className='h-12 w-1 rounded-full bg-neutral-black-12 opacity-0 group-hover/sidebar:opacity-100 transition-opacity duration-150' />
      </div>
      <CoPilot isCollapsed={isCollapsed} onCollapse={toggle} />
      {isCollapsed && (
        <div className='w-full text-center py-4'>
          <SidebarToggleButton direction='left' onClick={toggle} />
        </div>
      )}
    </aside>
  );
});
RightSidebarWrapper.displayName = 'RightSidebarWrapper';

const ResponsiveLayoutInner: React.FC<ResponsiveLayoutProps> = ({
  header,
  className,
  showImpersonatingBanner = true,
}) => {
  const [bannerHeight, setBannerHeight] = useState(0);
  const bannerRef = useRef<HTMLDivElement>(null);
  const { authenticatedUser } = useAuth();
  const [isBannerHidden, setIsBannerHidden] = useToggle();

  const mainContentRef = useRef<HTMLElement>(null);
  const [leftMarginClass, setLeftMarginClass] = useState('ml-72');
  const [rightMarginWidth, setRightMarginWidth] = useState('0px');

  const handleLeftMarginChange = useCallback((cls: string) => setLeftMarginClass(cls), []);
  const handleRightWidthChange = useCallback((w: string) => setRightMarginWidth(w), []);

  const hasConservationUi = useUIStyle(OrganizationUIStyle.CONSERVATION);

  useEffect(() => {
    const measureBannerHeight = () => {
      if (bannerRef.current) {
        setBannerHeight(bannerRef.current.offsetHeight);
      } else {
        setBannerHeight(0);
      }
    };

    measureBannerHeight();

    window.addEventListener('resize', measureBannerHeight);
    return () => window.removeEventListener('resize', measureBannerHeight);
  }, [isBannerHidden]);

  const height = `calc(100vh - ${bannerHeight}px)`;

  return (
    <div className={cn('min-h-screen flex flex-col overflow-hidden', className)}>
      {/* Top Banner - Sticky */}
      {showImpersonatingBanner && authenticatedUser.impersonatedBy && !isBannerHidden && (
        <div ref={bannerRef} className='sticky top-0 bg-primary text-primary-foreground'>
          <ImpersonatingBanner onDismiss={setIsBannerHidden} />
        </div>
      )}

      {/* Main Layout Container */}
      <div className='flex-1 flex relative overflow-hidden' style={{ height }}>
        <Suspense
          fallback={
            <LeftSidebarContainer bannerHeight={bannerHeight}>
              <Loader />
            </LeftSidebarContainer>
          }
        >
          <LeftSidebarWrapper bannerHeight={bannerHeight} onMarginChange={handleLeftMarginChange} />
        </Suspense>
        <main
          ref={mainContentRef}
          className={cn('flex-1 flex flex-col overflow-hidden transition-all duration-200', leftMarginClass)}
          data-testid='main-content'
          style={{ height, marginRight: rightMarginWidth }}
        >
          {header && <header className='border-b border-border flex-shrink-0 z-10'>{header}</header>}

          <div className='flex-1 overflow-y-auto hide-scrollbar'>
            <Suspense fallback={<Loader />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
        {!hasConservationUi && (
          <Suspense fallback={<Loader />}>
            <RightSidebarWrapper bannerHeight={bannerHeight} onWidthChange={handleRightWidthChange} />
          </Suspense>
        )}
      </div>
    </div>
  );
};

export const ResponsiveLayout = memo(ResponsiveLayoutInner);
